import React, {
	type MouseEvent,
	type KeyboardEvent,
	memo,
	type Dispatch,
	type SetStateAction,
} from 'react';
import type { MessageDescriptor } from 'react-intl-next';
import { listItemMenu } from '../../../../../../../common/constants';
import type {
	ListItemOptionId,
	ItemId,
	ItemDropPayload,
	OnCreateClick,
	ListItemOption,
} from '../../../../../../../common/types';
import type { SetCloseMenuCallback } from '../../../../../../../common/types/item';
import type { RenderListItemMenuOption } from '../../../../../../../renderers';
import { ListItemMenuOption } from '../../../list-item-menu-option';
import CreateItemMenu from './create-item-menu';
import MoveItemMenu from './move-item-menu';

type Props = {
	issuesSectionOptions: ListItemOption[];
	id: ItemId;
	level: number;
	depth: number;
	parentId: ItemId | undefined;
	overrideLabels: Partial<Record<ListItemOptionId, MessageDescriptor>>;
	isCreateChildEnabled: boolean;
	isCreateSiblingEnabled: boolean;
	onInlineCreateClicked: OnCreateClick;
	onDrop: (id: ItemId, payload: ItemDropPayload) => void;
	setInitialFocusRef?: Dispatch<SetStateAction<HTMLElement | null>>;
	renderListItemMenuOption: RenderListItemMenuOption;
	onCreateTriggerClick: () => void;
	setCloseMenu: SetCloseMenuCallback;
	setOpenModal: (event: MouseEvent | KeyboardEvent, optionId: string) => void;
};

const ItemSectionOptions = ({
	id,
	parentId,
	level,
	depth,
	overrideLabels,
	isCreateChildEnabled,
	isCreateSiblingEnabled,
	onInlineCreateClicked,
	issuesSectionOptions,
	setInitialFocusRef,
	renderListItemMenuOption,
	setCloseMenu,
	setOpenModal,
	onDrop,
	onCreateTriggerClick,
}: Props) =>
	issuesSectionOptions.map(({ id: optionId }, index) => {
		switch (optionId) {
			case listItemMenu.CREATE_ITEM:
				return (
					<CreateItemMenu
						key={optionId}
						id={id}
						parentId={parentId}
						level={level}
						depth={depth}
						isCreateChildEnabled={isCreateChildEnabled}
						isCreateSiblingEnabled={isCreateSiblingEnabled}
						overrideLabels={overrideLabels}
						onInlineCreateClicked={onInlineCreateClicked}
						setInitialFocusRef={index === 0 ? setInitialFocusRef : undefined}
						onCreateTriggerClick={onCreateTriggerClick}
						setCloseMenu={setCloseMenu}
					/>
				);
			case listItemMenu.MOVE_ITEM:
				return (
					<MoveItemMenu
						id={id}
						key={optionId}
						level={level}
						parentId={parentId}
						overrideLabels={overrideLabels}
						setInitialFocusRef={index === 0 ? setInitialFocusRef : undefined}
						onDrop={onDrop}
						setCloseMenu={setCloseMenu}
					/>
				);
			default:
				return renderListItemMenuOption({
					id,
					optionId,
					setOpenModal: (event) => setOpenModal(event, optionId),
					setInitialFocusRef: index === 0 ? setInitialFocusRef : undefined,
					Option: ListItemMenuOption,
					onCreateTriggerClick,
					setCloseMenu,
				});
		}
	});

export default memo<Props>(ItemSectionOptions);
